import type { CaseBookEntry, CaseData, CaseId, CaseStatus } from "./caseSchema";

const CASE_BOOK_KEY = "caseBook_v2";
const ACTIVE_CASE_KEY = "caseActiveCaseId";

type CaseBook = Record<CaseId, CaseBookEntry>;

function safeParseJson<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readBook(): CaseBook {
  return safeParseJson<CaseBook>(localStorage.getItem(CASE_BOOK_KEY), {});
}

function writeBook(book: CaseBook): void {
  localStorage.setItem(CASE_BOOK_KEY, JSON.stringify(book));
}

function generateCaseId(book: CaseBook, now = new Date()): CaseId {
  const prefix =
    String(now.getFullYear()) +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0") +
    String(now.getHours()).padStart(2, "0") +
    String(now.getMinutes()).padStart(2, "0");

  let seq = 1;
  while (book[prefix + String(seq).padStart(2, "0")]) seq++;
  return (prefix + String(seq).padStart(2, "0")) as CaseId;
}

export type SaveCaseResult = {
  caseId: CaseId;
  entry: CaseBookEntry;
  isUpdate: boolean;
};

export function saveCase(
  caseData: CaseData,
  options: { caseId?: CaseId; status?: CaseStatus } = {},
): SaveCaseResult {
  const book = readBook();

  const caseId = options.caseId ?? generateCaseId(book);
  const prev = book[caseId];

  const status: CaseStatus = options.status ?? prev?.status ?? "draft";
  const updatedAt = new Date().toISOString();

  const entry: CaseBookEntry = {
    caseId,
    status,
    updatedAt,
    data: caseData,
  };

  book[caseId] = entry;
  writeBook(book);
  localStorage.setItem(ACTIVE_CASE_KEY, caseId);

  return { caseId, entry, isUpdate: !!prev };
}

export function getCase(caseId: CaseId): CaseBookEntry | null {
  const book = readBook();
  return book[caseId] ?? null;
}

export function getAllCases(): CaseBookEntry[] {
  const book = readBook();
  return Object.values(book).sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
}

export function getActiveCaseId(): CaseId | null {
  const v = localStorage.getItem(ACTIVE_CASE_KEY);
  return v ? (v as CaseId) : null;
}
